import { Fancybox } from '@fancyapps/ui';
import axios from 'smg/axios';
import { SetBootstrapPrefix } from 'smg/bootstrap';
import { Browser } from 'smg/browser';
import { GetCartID } from 'smg/customer-api/carts';
import { GetPageRequestID } from 'smg/customer-api/pagerequests';
import * as session from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import { InitGoogleCredentialHandler } from 'smg/googlesignin';
import { SetIconLibrary } from 'smg/icons';
import Modal, { SetDefaults } from 'smg/modal';
import * as phones from 'smg/phone';
import { G } from 'smg/querystring';
import { SetDefaultToastClasses, SetDefaultToastCloseBtnClasses } from 'smg/toast';
import { tns } from 'tiny-slider';
import { Relref } from './hugo';
import { SwiperInit } from './swiper';

import './pricing';
import './time';

import * as carts from './carts';
import * as login from './login';

import './components/buttons';
import './components/cart-item-preview';
import './components/components';
import './components/dropdowns';
import './components/filters';
import './components/footer';
import './components/forms/form-checks';
import './components/forms/form-control';
import './components/gallery';
import './components/header';
import './components/jump-to-top';
import './components/product-type-quiz';
import './components/toasts';

import './pages/components/alerts-toasts';
import './pages/components/buttons';
import './pages/components/form';
import './pages/components/uploader';

import { Escape, FromString } from 'smg/html';
import './pages/account/need-help';
import './pages/account/order';
import './pages/account/orders';
import './pages/account/overview';
import './pages/account/profile-settings';
import './pages/cart';
import './pages/checkout';
import './pages/contact';
import './pages/discover/preview';
import './pages/faq-search';
import './pages/gallery';
import './pages/legal';
import './pages/order-success';
import './pages/pricing';
import './pages/product-detail';
import './pages/product-pages';
import './pages/quote-search';
import './pages/reset-password';
import './pages/revisions';
import './pages/search';
import './pages/trading-pin-get-started';
import './pages/usage';

declare interface myWindow extends Window {
    getPageRequestID: typeof GetPageRequestID;
}
declare const window: myWindow;

window.getPageRequestID = GetPageRequestID;

errors.InitClientSideErrorLogging();

SetDefaults({
    init: (modal: Modal<unknown>) => {
        const modalError = modal.Element.querySelector('.modal-error') as HTMLElement;
        const modalBody = modal.Element.querySelector('.modal-body') as HTMLElement;
        const modalFooter = modal.Element.querySelector('.modal-footer') as HTMLElement;
        modalFooter.classList.add('d-flex', 'justify-content-center', 'gap-4');
        modalError.className = 'd-none';
        modalError.classList.add('smg-modal-error', 'alert', 'alert-danger', 'mb-8', 'pb-5');
        modalBody?.insertBefore(modalError, modalBody.firstElementChild);
    },
});

errors.Defaults.ShowErrorFunc = msg => new Modal({
    size: 'modal-sm',
    body: FromString(`<div class="d-flex flex-column align-items-center text-center">
            <div class="icon-mark icon-mark-lg mb-6">
                <span class="material-symbols-outlined">error</span>
            </div>
            <h2 class="fw-900 mb-2">An error occurred</h2>
            <p class="mb-2">We encountered an unexpected issue. If this message continues to appear,
            please take a screenshot and contact our support team so we can help resolve it.</p>
            <code class="w-100 p-1 bg-light text-center">${Escape(msg)}</code>
        </div>`),
    cancelText: 'Ok',
    cancelButtonClass: 'btn btn-primary mx-auto',
    appendClassBySelector: {
        '.modal-body': 'px-6 pt-8 pb-2',
        '.modal-footer': 'p-6 pt-sm-10 pb-sm-14 justify-content-center',
    },
});

Fancybox.bind('[data-fancybox]', {
    Images: {
        content: (_, slide) => {
            const $picture = document.createElement('picture');

            if (slide.triggerEl?.dataset.srcsetWebp) {
                const $sourceWebp = document.createElement('source');
                $sourceWebp.srcset = slide.triggerEl.dataset.srcsetWebp;
                $sourceWebp.type = 'image/webp';
                $picture.appendChild($sourceWebp);
            }

            const $img = document.createElement('img');
            $img.src = String(slide.src ?? '');
            $picture.appendChild($img);

            if (slide.triggerEl?.dataset.srcset) {
                $img.srcset = slide.triggerEl.dataset.srcset;
            }

            return $picture;
        },
    },
});

SetIconLibrary('materialSymbolsRounded');

SetDefaultToastClasses(['align-items-center']);
SetDefaultToastCloseBtnClasses(['me-2', 'm-auto']);

SetBootstrapPrefix('smg-');

phones.SetDefaults({
    customPlaceholder: str => str,
});

SwiperInit();

async function initCart() {
    if (G().Get('deleteCart')) {
        await carts.DeleteCart().catch(() => undefined);
        G().Delete('deleteCart');
    }

    if (!GetCartID()) {
        return;
    }

    carts.
        GetCartCount().
        then(count => {
            if (!count) {
                return;
            }

            carts.IncrementCartCount(count);
        }).
        catch(err => console.error(`failed to get cart count: ${String(err)}`));
}

const sess = session.Session();

InitGoogleCredentialHandler();

const $accountButton = document.querySelectorAll<HTMLAnchorElement>('.account-button');
$accountButton.forEach($ => sess.then(() => {
    $.href = Relref('/account');
}).catch(() => {
    $.addEventListener('click', () => {
        login.PhoneLoginModal();
    });
}));

const $logoutButton = document.querySelectorAll<HTMLAnchorElement>('.logout-button');
$logoutButton.forEach($ => {
    $.addEventListener('click', async e => {
        e.preventDefault();

        await axios.delete('/sessions').then(() => {
            session.ClearLoginToken();
            session.ClearCache();
            window.location.href = Relref('/');
        });
    });
});

document.querySelectorAll('.hidden-with-signin').forEach($ => {
    void sess.then(() => {
        $.classList.add('d-none');
    });
});

document.querySelectorAll('.visible-with-signin').forEach($ => {
    void sess.then(() => {
        $.classList.remove('d-none');
    });
});

async function main() {
    const loginToken = G().Get('token');
    if (loginToken) {
        G().Delete('token');

        await session.CreateSessionWithLoginToken(loginToken).catch(err => console.warn(err));
    }

    const forgotPassword = G().Get('forgotPassword');
    if (typeof forgotPassword === 'string') {
        G().Delete('forgotPassword');

        login.ForgotPasswordLoginModal();
    }

    document.body.classList.add(`browser-${Browser()}`);

    void initCart();

    const $sliderContainer = document.querySelector<HTMLElement>('#slider-container');
    const $prevButton = document.querySelector<HTMLElement>('#prev-btn');
    const $nextButton = document.querySelector<HTMLElement>('#next-btn');
    if ($sliderContainer && $prevButton && $nextButton) {
        tns({
            container: $sliderContainer,
            items: 6,
            slideBy: 1,
            autoplay: false,
            autoplayButtonOutput: false,
            autoplayHoverPause: true,
            controls: true,
            nav: false,
            mouseDrag: true,
            loop: true,
            prevButton: $prevButton,
            nextButton: $nextButton,
        });
    }
}

void main();
